/**
 * Шаблоны сообщений: русские подстановки и линтер рекламы (4-Б6, ADR-037 §8.2, §8.5).
 *
 * ПОЧЕМУ ЭТО ЖИВЁТ В КОНТРАКТАХ, А НЕ В API. Тем же правилам подчиняются двое: сервер,
 * который отказывает в сохранении, и редактор, который подсвечивает ошибку по ходу
 * набора. Две копии правил расходятся на первой же правке, и расходятся молча: салон
 * видит в редакторе «всё хорошо», а сервер отказывает — или, что хуже, наоборот.
 *
 * ПОДСТАНОВКИ ПО-РУССКИ — НЕ УКРАШЕНИЕ. Администратор салона набирает текст, который
 * прочитает его клиент, и `{{client.name}}` в этом тексте — чужой язык: он его либо
 * не наберёт, либо наберёт с ошибкой и не поймёт, почему имя не подставилось. Внутри
 * мы храним канонические ключи (§8.2), а наружу показываем `{имя}` — ровно как в макете.
 *
 * ОДНИ ФИГУРНЫЕ СКОБКИ СНАРУЖИ, ДВОЙНЫЕ ВНУТРИ. В макете подстановка выглядит как
 * `{имя}`, и это правильно для человека: двойные скобки он наберёт неверно. Внутри
 * двойные остаются, потому что по ним уже написан рендерер и все системные шаблоны.
 *
 * НЕИЗВЕСТНАЯ ПОДСТАНОВКА — ОТКАЗ С ПОДСКАЗКОЙ, А НЕ ПУСТОТА. Текст с `{имя_мастера}`
 * уходил бы клиенту с дырой посреди предложения, и салон узнавал бы об этом от клиента.
 * Поэтому сохранение отклоняется, прежний текст продолжает работать, а в ответе лежит
 * название ближайшей существующей подстановки: «возможно, вы имели в виду {мастер}».
 */
/**
 * Русские имена подстановок — дословно из макета 4-Б6.
 *
 * ССЫЛКА ОДНА, А НЕ ДВЕ. В макете их две — `{ссылка_перенос}` и `{ссылка_отмена}`, —
 * но продукт после решения владельца 51 выпускает ОДНУ ссылку на карточку записи,
 * с которой доступны оба действия. Причина не в экономии: в SMS помещается одна ссылка
 * из двух, а подпись — это обещание, и ссылка «отменить», открывающая просмотр, стоит
 * салону звонка от клиента, решившего, что отмена не сработала. Обе ссылки из макета
 * остаются УЗНАВАЕМЫМИ (см. PLACEHOLDER_HINTS): салон, набравший их, получает не отказ
 * «такой подстановки нет», а объяснение, что делать.
 */
export const TEMPLATE_PLACEHOLDERS = [
    { ru: 'имя', key: 'client.name', title: 'имя клиента' },
    { ru: 'дата', key: 'appointment.date', title: 'дата визита' },
    { ru: 'время', key: 'appointment.time', title: 'время визита' },
    { ru: 'услуга', key: 'appointment.services', title: 'услуги визита' },
    { ru: 'цена', key: 'appointment.price', title: 'стоимость визита' },
    { ru: 'мастер', key: 'employee.name', title: 'имя мастера' },
    { ru: 'филиал', key: 'branch.name', title: 'название филиала' },
    { ru: 'адрес', key: 'branch.address', title: 'адрес филиала' },
    { ru: 'телефон_филиала', key: 'branch.phone', title: 'телефон филиала' },
    { ru: 'ссылка', key: 'link.manage', title: 'ссылка на запись: перенос и отмена' },
    { ru: 'ссылка_отписки', key: 'link.unsubscribe', title: 'ссылка отказа от рассылки' },
];
const BY_RU = new Map(TEMPLATE_PLACEHOLDERS.map((item) => [item.ru, item]));
const BY_KEY = new Map(TEMPLATE_PLACEHOLDERS.map((item) => [item.key, item]));
/**
 * Подсказки для того, что салон напишет вместо существующей подстановки.
 *
 * СПИСОК НЕ ЗАМЕНЯЕТ ПОИСК ПОХОЖЕГО, А ДОПОЛНЯЕТ ЕГО. Поиск по расстоянию находит
 * опечатки («мастре»), но не находит смысловых промахов: «имя_мастера» отличается
 * от «мастер» на семь символов и ближе к «имя», то есть без этого списка салон получил
 * бы совет подставить имя КЛИЕНТА вместо имени мастера — подсказку хуже молчания.
 */
const PLACEHOLDER_HINTS = {
    имя_мастера: 'мастер',
    мастера: 'мастер',
    имя_клиента: 'имя',
    клиент: 'имя',
    дата_записи: 'дата',
    время_записи: 'время',
    услуги: 'услуга',
    телефон: 'телефон_филиала',
    салон: 'филиал',
    точка: 'филиал',
    стоимость: 'цена',
    сумма: 'цена',
    ссылка_перенос: 'ссылка',
    ссылка_переноса: 'ссылка',
    ссылка_отмена: 'ссылка',
    ссылка_отмены: 'ссылка',
    отписка: 'ссылка_отписки',
    /*
     * ЧАСТЫЕ СМЫСЛОВЫЕ ПРОМАХИ, КОТОРЫЕ ПОИСК ПО ПОХОЖЕСТИ РАЗБИРАЕТ НЕВЕРНО.
     *
     * «ссылка» ближе всего к «ссылка_отписки» по буквам, поэтому человек, написавший
     * `{ссылка_записи}`, получал совет подставить ссылку ОТКАЗА ОТ РАССЫЛКИ — то есть
     * совет, который портит напоминание. А «телефон_клиента» по буквам ближе к
     * «телефон_филиала», и совет выглядел разумным, хотя телефона клиента у нас
     * в подстановках нет вовсе и быть не должно (§8.2): текст SMS уходит агрегатору.
     */
    ссылка_записи: 'ссылка',
    ссылка_визита: 'ссылка',
    телефон_клиента: null,
    номер_клиента: null,
    мобильный: null,
};
/** Подстановки, доступные салону для этого события: русские имена по ключам события. */
export function placeholdersFor(keys) {
    const allowed = new Set(keys);
    return TEMPLATE_PLACEHOLDERS.filter((item) => allowed.has(item.key));
}
// ───────────────────────── перевод туда и обратно ─────────────────────────
/*
 * ЧТО СЧИТАЕТСЯ ПОПЫТКОЙ ПОДСТАНОВКИ. Всё, что человек написал в фигурных скобках, —
 * и это шире, чем «буквы и подчёркивание».
 *
 * Прежняя запись `[a-zа-яё_]+` не узнавала ни `{имя мастера}` (пробел), ни
 * `{имя-мастера}` (дефис), ни `{цена2}` (цифра). Такая скобка не разворачивалась
 * и НЕ попадала в список неизвестных: сохранение проходило, а клиент получал
 * «Вы записаны к {имя мастера}» — фигурные скобки посреди предложения. Самый частый
 * способ ошибиться оказался единственным, о котором мы молчали.
 *
 * Внутрь скобок не пускаем сами скобки, а длину ограничиваем: `{` в тексте бывает
 * и просто знаком, и жадное совпадение съело бы полфразы, превратив её в «неизвестную
 * подстановку» целиком.
 */
const RU_RE = /\{\s*([^{}\n]{1,60}?)\s*\}/gu;
/*
 * ДВОЙНЫЕ СКОБКИ ОТ ЧЕЛОВЕКА — ЭТО ТЕ ЖЕ ОДИНАРНЫЕ. В макете подстановка выглядит
 * как `{имя}`, но человек, видевший вёрстку писем, пишет `{{имя}}`. Разбор находил
 * внутри `{имя}`, подставлял ключ и оставлял лишнюю пару: в базе получалось
 * `{{{client.name}}}`, а клиент читал `{Мария}`.
 */
const DOUBLE_RE = /\{\{\s*([^{}\n]{1,60}?)\s*\}\}/gu;
const KEY_RE = /\{\{\s*([a-z_]+\.[a-z_]+)\s*\}\}/gu;
/**
 * Расстояние Дамерау — Левенштейна, ограниченное сверху.
 *
 * ОГРАНИЧЕНИЕ СВЕРХУ — НЕ ОПТИМИЗАЦИЯ, А СМЫСЛ. Подсказка «возможно, вы имели в виду»
 * полезна, пока слова действительно похожи. Совет заменить «промокод» на «мастер»
 * (расстояние 7 из 8) не помогает, а сбивает: салон решит, что подстановки для промокода
 * у нас называются «мастер».
 */
function distance(a, b, limit) {
    if (Math.abs(a.length - b.length) > limit)
        return limit + 1;
    let previous = Array.from({ length: b.length + 1 }, (_, index) => index);
    for (let i = 1; i <= a.length; i += 1) {
        const row = [i];
        for (let j = 1; j <= b.length; j += 1) {
            const cost = a[i - 1] === b[j - 1] ? 0 : 1;
            row.push(Math.min((previous[j] ?? Infinity) + 1, (row[j - 1] ?? Infinity) + 1, (previous[j - 1] ?? Infinity) + cost));
        }
        previous = row;
    }
    return previous[b.length] ?? limit + 1;
}
/** Ближайшее существующее русское имя подстановки или null. */
export function suggestPlaceholder(raw) {
    const normalized = raw.trim().toLowerCase().replaceAll(' ', '_').replaceAll('-', '_');
    if (BY_RU.has(normalized))
        return normalized;
    if (normalized in PLACEHOLDER_HINTS) {
        // null означает «такой подстановки у нас нет и не будет»: телефон клиента наружу
        // не отдаётся вовсе, и подсказывать вместо него телефон филиала значит советовать
        // отправить клиенту чужой номер.
        return PLACEHOLDER_HINTS[normalized] ?? null;
    }
    // Порог в треть длины: «мастре» → «мастер» находится, «промокод» → «мастер» нет.
    const limit = Math.max(1, Math.floor(normalized.length / 3));
    let best = null;
    let bestDistance = limit + 1;
    for (const item of TEMPLATE_PLACEHOLDERS) {
        const value = distance(normalized, item.ru, limit);
        if (value < bestDistance) {
            bestDistance = value;
            best = item.ru;
        }
    }
    return bestDistance <= limit ? best : null;
}
/**
 * Текст из редактора → текст для базы.
 *
 * НЕИЗВЕСТНЫЕ ПОДСТАНОВКИ ОСТАЮТСЯ В ТЕКСТЕ КАК БЫЛИ. Выбрасывать их нельзя: сохранение
 * всё равно будет отклонено, а салон должен увидеть в редакторе ровно то, что набрал,
 * — иначе он не поймёт, что именно исправлять.
 */
export function expandPlaceholders(text) {
    const unknown = [];
    const seen = new Set();
    // Сначала двойные скобки приводятся к одинарным: иначе от них остаётся лишняя пара.
    const normalized = text.replace(DOUBLE_RE, (_match, rawName) => `{${rawName}}`);
    const body = normalized.replace(RU_RE, (match, rawName) => {
        const name = rawName.trim().toLowerCase();
        const found = BY_RU.get(name);
        if (found !== undefined)
            return `{{${found.key}}}`;
        if (!seen.has(name)) {
            seen.add(name);
            unknown.push({ raw: rawName, suggestion: suggestPlaceholder(name) });
        }
        return match;
    });
    return { body, unknown };
}
/** Текст из базы → текст для редактора: канонические ключи обратно в русские имена. */
export function humanizePlaceholders(body) {
    return body.replace(KEY_RE, (match, key) => {
        const found = BY_KEY.get(key);
        return found === undefined ? match : `{${found.ru}}`;
    });
}
/**
 * Рекламные признаки в транзакционном тексте.
 *
 * ЗАЧЕМ ЭТО ВООБЩЕ ПРОВЕРЯТЬ. Напоминание о записи уходит ВСЕМ клиентам: согласие
 * на такое сообщение не требуется, потому что оно об их собственном визите. Стоит
 * приписать к нему «скидка 20 % на окрашивание» — и то же самое сообщение становится
 * рекламной рассылкой (ч. 1 ст. 18 38-ФЗ), а отправлять её можно только тем, кто дал
 * согласие на рекламу. Цена ошибки — штраф по ст. 14.3 КоАП, 100–500 тыс. ₽, и платит
 * его салон, а объяснять, почему мы промолчали, придётся нам.
 *
 * ПОЧЕМУ ЭТО ПРЕДУПРЕЖДЕНИЕ, А НЕ ЗАПРЕТ. Отличить рекламу от уместного упоминания
 * автоматически нельзя: «скидка постоянного клиента уже учтена» — не реклама, а часть
 * расчёта. Решение остаётся за салоном, наше дело — назвать риск и предложить вынести
 * текст в отдельную рассылку, где согласие проверяется.
 */
const PROMO_RULES = [
    {
        re: /скидк\p{L}*[^.!?;]{0,40}/giu,
        why: 'скидка — это предложение товара, а не сведения о визите',
    },
    {
        re: /акци\p{L}*[^.!?;]{0,40}/giu,
        why: 'акция адресуется всем, а не конкретному клиенту с записью',
    },
    { re: /распродаж\p{L}*[^.!?;]{0,40}/giu, why: 'распродажа — реклама по определению' },
    {
        re: /промокод\p{L}*[^.!?;]{0,40}/giu,
        why: 'промокод выдают для покупки, а не для напоминания о визите',
    },
    {
        // Корень плюс любое окончание: «дарим», «подарим», «подарок», «подарки», «в подарок».
        re: /(?:дар\p{L}*|подар\p{L}*)[^.!?;]{0,40}/giu,
        why: 'подарок за покупку — стимулирующее предложение',
    },
    {
        re: /бесплатн\p{L}*[^.!?;]{0,40}/giu,
        why: '«бесплатно» в тексте о визите читается как предложение новой услуги',
    },
    {
        re: /(?:только сегодня|успей\p{L}*|спешите|последний день)[^.!?;]{0,40}/giu,
        why: 'срочность создают в рекламе, чтобы подтолкнуть к покупке',
    },
    {
        /*
         * ПРОБЕЛ МЕЖДУ СЛОВАМИ ОБЯЗАТЕЛЕН. Раньше здесь стояло `выгодн\p{L}*предложен\p{L}*`
         * без пробела, то есть правило требовало слова «выгодноепредложение» слитно
         * и не срабатывало НИКОГДА. Проверка выглядела работающей и молчала.
         */
        re: /(?:спецпредложен\p{L}*|выгодн\p{L}*\s+предложен\p{L}*)[^.!?;]{0,40}/giu,
        why: 'предложение услуги — это реклама',
    },
    {
        re: /-\s?\d{1,2}\s?%[^.!?;]{0,40}/giu,
        why: 'процент со знаком минус означает скидку',
    },
];
/**
 * Версия набора правил линтера.
 *
 * ХРАНИТСЯ ВМЕСТЕ С ШАБЛОНОМ, и не «для истории»: когда список правил расширят,
 * постмодерации надо знать, какие шаблоны проверялись прошлыми правилами, а какие
 * нынешними. Без этого перепроверять придётся всё подряд — то есть не перепроверять
 * вовсе. Меняется вместе с PROMO_RULES и тестами.
 */
export const PROMO_RULESET_VERSION = '2026-09';
/** Проверить текст на рекламные признаки. Пустой список — признаков не найдено. */
export function lintTemplate(text) {
    const promo = [];
    const seen = new Set();
    for (const rule of PROMO_RULES) {
        for (const match of text.matchAll(rule.re)) {
            const quote = match[0].trim();
            const key = quote.toLowerCase();
            if (quote.length === 0 || seen.has(key))
                continue;
            seen.add(key);
            promo.push({ quote, why: rule.why });
        }
    }
    return { performed: true, promo };
}
/**
 * Фраза предупреждения для экрана — дословно из макета 4-Б6.
 *
 * ТЕКСТ ОДИН НА СЕРВЕР И КАБИНЕТ. Предупреждение показывается и в редакторе по ходу
 * набора, и в ответе сервера при сохранении: две редакции одной мысли читаются как две
 * разные проверки, и салон начинает искать, какая из них главная.
 */
export function promoWarning(finding) {
    return (`Похоже на рекламу: «${finding.quote}». Напоминание о записи с рекламой внутри ` +
        'считается рекламной рассылкой, и отправлять её можно только тем, кто дал согласие ' +
        'на рекламу.');
}
/** Фраза об неизвестной подстановке — дословно из макета 4-Б6. */
export function unknownPlaceholderWarning(item) {
    const head = `В тексте есть подстановка {${item.raw}}, которой у нас нет`;
    const tail = 'Пока не исправите, шаблон не сохранится и продолжит работать прежний.';
    return item.suggestion === null
        ? `${head}. ${tail}`
        : `${head} — возможно, вы имели в виду {${item.suggestion}}. ${tail}`;
}
/**
 * Системные тексты по событиям клиента.
 *
 * РЕКЛАМНЫЕ СОБЫТИЯ НЕСУТ МЕХАНИЗМ ОТКАЗА В КАЖДОМ КАНАЛЕ. День рождения и «давно
 * не были» — реклама (ч. 1 ст. 18 38-ФЗ), и без механизма отказа они не уходят вовсе:
 * проверка стоит и в линтере шаблона, и на выпуске. В SMS механизм отказа — слово СТОП
 * (§7.4), потому что ссылка съела бы отдельную часть и удвоила цену; в остальных
 * каналах — ссылка `{ссылка_отписки}`. Сторож `template-contract.test.ts` проверяет
 * это для каждого события и каждого канала: раньше короткий текст не нёс ни того,
 * ни другого, и рекламное SMS с пушем молча не отправлялись.
 *
 * СОБЫТИЯ САЛОНА ЗДЕСЬ НЕ НАЗВАНЫ. Их текст читает сотрудник в центре уведомлений,
 * а не клиент в SMS, и правит его не салон: «у вас кончаются деньги» салон не должен
 * уметь переписать так, чтобы не понять. Рассылка тоже не названа: её текст принадлежит
 * кампании, а не шаблону события (см. template-source.ts).
 */
export const SYSTEM_TEMPLATES = [
    {
        event: 'appointment.created',
        title: 'Запись создана',
        short: 'Вы записаны {дата} в {время}. Перенести: {телефон_филиала}',
        long: 'Здравствуйте, {имя}! Вы записаны: {дата} в {время}, {услуга}, мастер {мастер}. ' +
            '{филиал}, {адрес}. Перенести или отменить можно здесь: {ссылка}',
        subject: 'Вы записаны на {дата}',
    },
    {
        event: 'appointment.reminder',
        title: 'Напоминание о визите',
        short: 'Напоминаем: {дата} в {время}. Перенести: {телефон_филиала}',
        long: 'Здравствуйте, {имя}! Напоминаем: сегодня в {время} вы записаны на {услуга} ' +
            'к мастеру {мастер}. {филиал}, {адрес}. Если планы изменились — перенесите или ' +
            'отмените заранее: {ссылка}',
        subject: 'Напоминание о записи {дата}',
    },
    {
        event: 'appointment.rescheduled',
        title: 'Запись перенесена',
        short: 'Запись перенесена на {дата} {время}. Вопросы: {телефон_филиала}',
        long: 'Здравствуйте, {имя}! Ваша запись перенесена: теперь {дата} в {время}, {услуга}, ' +
            'мастер {мастер}. {филиал}, {адрес}. Посмотреть или перенести снова: {ссылка}',
        subject: 'Запись перенесена на {дата}',
    },
    {
        event: 'appointment.cancelled',
        title: 'Запись отменена',
        short: 'Запись {дата} {время} отменена. Звоните: {телефон_филиала}',
        long: 'Здравствуйте, {имя}! Ваша запись {дата} в {время} отменена. Если это недоразумение ' +
            'или хотите выбрать другое время — позвоните нам: {телефон_филиала}.',
        subject: 'Запись {дата} отменена',
    },
    {
        event: 'appointment.confirm_request',
        title: 'Просьба подтвердить запись',
        short: 'Подтвердите запись {дата} в {время}: {телефон_филиала}',
        long: 'Здравствуйте, {имя}! Подтвердите, пожалуйста, запись {дата} в {время} — ' +
            '{услуга}, мастер {мастер}. Подтвердить или перенести: {ссылка}',
        subject: 'Подтвердите запись на {дата}',
    },
    {
        /*
         * НИ СЛОВА УПРЁКА. Человек, не пришедший на визит, чаще всего не смог, а не забыл;
         * сообщение «вы не пришли» с укором стоит салону клиента, а не дисциплины. Наше дело
         * — дать знать, что время закрыто, и оставить способ связаться.
         */
        event: 'appointment.no_show',
        title: 'Отметка «не пришёл»',
        short: 'Вас не было на записи {дата}. Звоните: {телефон_филиала}',
        long: 'Здравствуйте, {имя}! Отметили, что вас не было на записи {дата} в {время}. ' +
            'Если это ошибка или хотите выбрать другое время — позвоните: {телефон_филиала}.',
        subject: 'Запись {дата}',
    },
    {
        event: 'review.request',
        title: 'Просьба оценить визит',
        short: 'Спасибо, что были у нас {дата}! Как всё прошло?',
        long: 'Здравствуйте, {имя}! Спасибо, что были у нас {дата} — {услуга}, мастер {мастер}. ' +
            'Расскажите, как всё прошло: нам это важно. Позвонить: {телефон_филиала}.',
        subject: 'Как прошёл ваш визит {дата}?',
    },
    {
        event: 'client.birthday',
        title: 'Поздравление с днём рождения',
        // СТОП — механизм отказа для SMS (§7.4): ссылка отписки съела бы отдельную часть,
        // то есть удвоила цену каждого поздравления.
        short: 'С днём рождения! Будем рады видеть вас: {телефон_филиала}. СТОП — отказ',
        long: 'Здравствуйте, {имя}! {филиал} поздравляет вас с днём рождения. Будем рады ' +
            'видеть вас: {адрес}, {телефон_филиала}. Отказаться от таких сообщений: {ссылка_отписки}',
        subject: 'С днём рождения!',
    },
    {
        event: 'client.dormant',
        title: 'Давно не были',
        short: 'Давно вас не видели! Записаться: {телефон_филиала}. СТОП — отказ',
        long: 'Здравствуйте, {имя}! Давно вас не видели в {филиал}. Будем рады видеть снова: ' +
            '{адрес}, {телефон_филиала}. Отказаться от таких сообщений: {ссылка_отписки}',
        subject: 'Давно вас не видели',
    },
];
const SYSTEM_BY_EVENT = new Map(SYSTEM_TEMPLATES.map((item) => [item.event, item]));
/**
 * Каналы, где место бесплатно, а значит уместен полный текст.
 *
 * ПУШ ЗДЕСЬ ПОТОМУ, ЧТО ПЛАТИМ МЫ ТОЛЬКО ЗА SMS. Короткий текст придуман ради денег:
 * кириллица режет SMS по 70 знаков, и каждая часть стоит отдельно. В пуше длина
 * не стоит ничего, зато есть требование, которого короткий текст выполнить не может:
 * рекламное сообщение обязано нести механизм отказа (ч. 1 ст. 18 38-ФЗ), а в пуше
 * это ССЫЛКА — словом СТОП на пуш не ответишь. С коротким текстом рекламный пуш
 * не уходил вовсе.
 *
 * Пуш обрезается на показе, поэтому в длинном тексте главное стоит В НАЧАЛЕ: время
 * и суть визита — в первой фразе, подробности и ссылка — дальше.
 */
const LONG_CHANNELS = new Set(['email', 'telegram', 'max', 'push']);
/**
 * Системный текст для пары «событие × канал» в РУССКИХ подстановках.
 *
 * null означает, что событие не правится салоном (рассылка, события сотрудника) —
 * и это ответ, а не отсутствие данных: пустая строка в редакторе читалась бы как
 * «системного текста нет», и салон написал бы свой на месте юридически вычитанного.
 */
export function systemTemplateBody(event, channel) {
    const found = SYSTEM_BY_EVENT.get(event);
    if (found === undefined)
        return null;
    return LONG_CHANNELS.has(channel) ? found.long : found.short;
}
/** Тема письма по событию. Только для e-mail. */
export function systemTemplateSubject(event) {
    return SYSTEM_BY_EVENT.get(event)?.subject ?? null;
}
/** Как событие называется салону: «Напоминание о визите». */
export function systemTemplateTitle(event) {
    return SYSTEM_BY_EVENT.get(event)?.title ?? null;
}
/** Событие правится салоном: у него есть системный текст. */
export function isEditableEvent(event) {
    return SYSTEM_BY_EVENT.has(event);
}
//# sourceMappingURL=notification-templates.js.map